import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ArrowLeft, Coins, CalendarClock, SlidersHorizontal, Save } from 'lucide-react';
import { InputField, CheckboxField, FormField } from '@/components/common/form-fields';
import { SkeletonLoader } from '@/components/ui/SkeletonLoader';
import { creditPlanService } from '@/services/credit-plan.service';
import { ADMIN_CREDIT_PLANS } from '@/constants/constant-routes';
import type { CreateCreditPlanRequest } from '@/types/credit-plan.types';

interface CreditPlanFormValues {
  plan_name: string;
  plan_key: string;
  description: string;
  credits: number;
  validity_days: number;
  rollover_unused_credits: boolean;
  auto_renew: boolean;
}

const schema = yup.object().shape({
  plan_name: yup.string().trim().required('Please enter a plan name'),
  plan_key: yup.string().trim().required('Please enter a plan key'),
  description: yup.string().trim().required('Please enter a short description'),
  credits: yup.number()
    .transform((v, o) => (o === '' ? undefined : v))
    .typeError('Credits must be a number')
    .min(0, 'Credits cannot be negative')
    .required('Please enter the number of credits'),
  validity_days: yup.number()
    .transform((v, o) => (o === '' ? undefined : v))
    .typeError('Validity must be a number')
    .integer('Validity must be a whole number of days')
    .min(1, 'Validity must be at least 1 day')
    .required('Please enter the validity in days'),
  rollover_unused_credits: yup.boolean().required(),
  auto_renew: yup.boolean().required(),
});

const CREATE_DEFAULTS = {
  plan_name: '', plan_key: '', description: '',
  credits: undefined, validity_days: undefined,
  rollover_unused_credits: false, auto_renew: false,
} as unknown as CreditPlanFormValues;

export default function CreditPlanFormPage() {
  const { plan_key } = useParams<{ plan_key?: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = !!plan_key;

  // Load the plan on edit.
  const { data: planResp, isLoading } = useQuery({
    queryKey: ['credit-plan', plan_key],
    queryFn: () => creditPlanService.getByKey(plan_key!),
    enabled: isEdit,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CreditPlanFormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: CREATE_DEFAULTS,
    values: isEdit ? (planResp as any)?.data : undefined, // resets when the edit data arrives
    mode: 'onBlur',
  });

  const errMsg = (error: any, fallback: string) =>
    error?.response?.data?.error?.error_description ||
    error?.response?.data?.error?.message ||
    error?.message ||
    fallback;

  const backToList = () => navigate(ADMIN_CREDIT_PLANS);

  const createMutation = useMutation({
    mutationFn: (payload: CreateCreditPlanRequest) => creditPlanService.create(payload),
    onSuccess: () => {
      toast.success('Credit plan created.');
      queryClient.invalidateQueries({ queryKey: ['credit-plans'] });
      backToList();
    },
    onError: (error: any) => toast.error(errMsg(error, 'Failed to create plan.')),
  });

  const updateMutation = useMutation({
    mutationFn: (payload: Partial<CreateCreditPlanRequest>) => creditPlanService.update(plan_key!, payload),
    onSuccess: () => {
      toast.success('Credit plan updated.');
      queryClient.invalidateQueries({ queryKey: ['credit-plans'] });
      backToList();
    },
    onError: (error: any) => toast.error(errMsg(error, 'Failed to update plan.')),
  });

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const onSubmit = (values: CreditPlanFormValues) => {
    const base = {
      plan_name: values.plan_name.trim(),
      description: values.description.trim(),
      credits: Number(values.credits),
      validity_days: Number(values.validity_days),
      rollover_unused_credits: !!values.rollover_unused_credits,
      auto_renew: !!values.auto_renew,
    };
    if (isEdit) {
      updateMutation.mutate(base); // plan_key not sent — immutable
    } else {
      createMutation.mutate({ ...base, plan_key: values.plan_key.trim() });
    }
  };

  if (isEdit && isLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-violet-600 to-purple-600 rounded-xl p-6">
          <SkeletonLoader type="title" className="bg-white/20 mb-2" />
          <SkeletonLoader type="text" width="50%" className="bg-white/20" />
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border">
          <SkeletonLoader type="title" className="mb-6" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i}><SkeletonLoader type="text" width="30%" className="mb-2" /><SkeletonLoader type="custom" className="h-12 w-full rounded-lg" /></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header — violet/purple theme */}
      <div className="bg-gradient-to-r from-violet-600 to-purple-600 rounded-xl p-6 text-white">
        <button onClick={backToList} className="inline-flex items-center gap-1 text-violet-100 hover:text-white mb-3 text-sm">
          <ArrowLeft className="w-4 h-4" /> Back to Credit Plans
        </button>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg"><Coins className="w-7 h-7" /></div>
          <div>
            <h1 className="text-2xl font-bold">{isEdit ? 'Edit Credit Plan' : 'Create Credit Plan'}</h1>
            <p className="text-violet-100">
              {isEdit ? 'Update the credit allotment, validity and behavior.' : 'Define a new credit plan.'}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Plan details */}
        <div className="card">
          <div className="flex items-center gap-2 mb-6">
            <Coins className="w-5 h-5 text-violet-600" />
            <h2 className="text-lg font-semibold">Plan Details</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InputField
              label="Plan Name"
              required
              placeholder="e.g. Standard Plan"
              error={errors.plan_name?.message}
              {...register('plan_name')}
            />
            <InputField
              label={isEdit ? 'Plan Key (not editable)' : 'Plan Key'}
              required
              readOnly={isEdit}
              placeholder="e.g. standard_plan"
              error={errors.plan_key?.message}
              className={isEdit ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}
              {...register('plan_key')}
            />
          </div>
          <div className="mt-6">
            <FormField label="Description" required error={errors.description?.message}>
              <textarea
                rows={3}
                placeholder="What this plan grants…"
                className={`w-full input-field resize-none ${errors.description ? 'border-red-500 focus:ring-red-500' : ''}`}
                {...register('description')}
              />
            </FormField>
          </div>
        </div>

        {/* Credits & validity */}
        <div className="card">
          <div className="flex items-center gap-2 mb-6">
            <CalendarClock className="w-5 h-5 text-violet-600" />
            <h2 className="text-lg font-semibold">Credits &amp; Validity</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InputField label="Credits" required fieldType="number" min={0} placeholder="1000" error={errors.credits?.message} {...register('credits')} />
            <InputField label="Validity (days)" required fieldType="number" min={1} placeholder="30" error={errors.validity_days?.message} {...register('validity_days')} />
          </div>
        </div>

        {/* Behavior */}
        <div className="card">
          <div className="flex items-center gap-2 mb-6">
            <SlidersHorizontal className="w-5 h-5 text-violet-600" />
            <h2 className="text-lg font-semibold">Behavior</h2>
          </div>
          <div className="space-y-4">
            <CheckboxField checkboxLabel="Auto-renew" checkboxDescription="Re-grant credits and restart the period when validity ends." {...register('auto_renew')} />
            <CheckboxField checkboxLabel="Rollover unused credits" checkboxDescription="Carry any leftover credits into the next period." {...register('rollover_unused_credits')} />
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button type="button" onClick={backToList} disabled={isSaving} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50">
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="inline-flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-lg font-semibold hover:from-violet-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-4 h-4" />
            {isSaving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Plan'}
          </button>
        </div>
      </form>
    </div>
  );
}
